import { Input, notification, Typography } from 'antd';
import { addCMSMedia } from 'api/cms-media.api';
import classNames from 'classnames';
import FormField from 'components/FormField';
import { MediaSelector } from 'components/MediaSelector';
import { PurchaseStockPhotoWarningModal } from 'components/StockPhotoWarning';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import {
    HIGHLIGHT_COVER,
    HIGHLIGHT_COVER_RATIO,
} from 'constants/media-sizes.constants';
import { useFormDirtyState } from 'hooks/use-form-dirty-state';
import React, { useCallback, useState } from 'react';
import { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { SortableList } from '../GuideEditor/SortableList';
import { useSort } from '../GuideEditor/SortableList/use-sort';
import { useDestinationEditor } from './DestinationEditorContext';
import styles from './DestinationHighlights.module.css';
import { useSyncedDestinationContents } from './use-synced-destination-contents';

import { deleteDestinationContent } from 'api/destinations.api';
import type { UpdateTranslationsValue } from 'hooks/use-destination-content-translation';
import { useDestinationContentTranslation } from 'hooks/use-destination-content-translation';
import type { DestinationContentFieldErrors } from 'hooks/use-destination-content-validation';
import { useDestinationContentValidation } from 'hooks/use-destination-content-validation';

import type { UpdateDestinationContentFieldChange } from './DestinationEditorContext';
import { getSourceId } from 'api/sources.api';
import {
    GUIDE_HIGHLIGHT_DESCRIPTION_MAX_LENGTH,
    GUIDE_HIGHLIGHT_TITLE_MAX_LENGTH,
} from 'constants/fields-limits';
import { DestinationEditorTabActions } from './DestinationEditorTabActions';

type DestinationEditorHighlightsProps = Readonly<{
    onClickDiscard: (hasUnsavedChanges: boolean) => void;
}>;

export function DestinationEditorHighlights({
    onClickDiscard,
}: DestinationEditorHighlightsProps) {
    const {
        destination,
        saveBulkContent,
        updateDestination,
    } = useDestinationEditor();
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
    ] = useState(false);
    const [imagesToPurchase, setImagesToPurchase] = useState<CMSMedia[]>([]);
    const [highlights, setHighlights] = useSyncedDestinationContents(
        DestinationContentTypes.HIGHLIGHT,
    );
    const [isSubmitted, setIsSubmitted] = useState(false);
    const {
        fieldErrors,
        validateFields,
    } = useDestinationContentValidation(highlights, ['title']);
    const { onMoveUp, onMoveDown } = useSort(highlights);

    const { isDirty, updateOriginalValues } = useFormDirtyState({
        values: highlights,
    });

    const handleUpdateFields = (
        changes: UpdateDestinationContentFieldChange[],
    ) => {
        let updatedHighlights = highlights ?? [];
        for (const change of changes) {
            updatedHighlights = updatedHighlights.map(
                (item: DestinationContentSchema) =>
                    item.id === change.id
                        ? { ...item, [change.fieldName]: change.newValue }
                        : item,
            );
        }
        setHighlights(updatedHighlights);

        if (isSubmitted) {
            validateFields(updatedHighlights);
        }
    };

    const handleUpdateTranslationField = ({
        contentId,
        translations,
    }: UpdateTranslationsValue) => {
        setHighlights((prev) =>
            prev.map((highlight) =>
                highlight.id === contentId
                    ? {
                          ...highlight,
                          translations,
                      }
                    : highlight,
            ),
        );
    };

    const handleUpdateField = (change: UpdateDestinationContentFieldChange) => {
        handleUpdateFields([change]);
    };

    const handleAdd = () => {
        if (!destination.id) {
            return;
        }

        const newHighlight = getEmptyHighlight(
            destination.id,
            highlights.length,
        );

        const updatedHighlights = highlights.concat([newHighlight]);

        setHighlights(updatedHighlights);
    };

    const handleDelete = async ({ id }: DestinationContentSchema) => {
        try {
            setIsDeleting(true);

            if (id > 0) {
                await deleteDestinationContent(id);
            }

            setHighlights((prev) =>
                prev
                    .filter((item) => item.id !== id)
                    .map((item, index) => ({
                        ...item,
                        order: index,
                    })),
            );
        } catch (error) {
            notification.error({
                message: 'Error deleting highlight',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMoveUp = (highlight: DestinationContentSchema) => {
        const sortedItems = onMoveUp(highlight);
        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>(
                    (item) => ({
                        id: item.id,
                        fieldName: 'order',
                        newValue: item.newOrder,
                    }),
                ),
            );
        }
    };

    const handleMoveDown = (highlight: DestinationContentSchema) => {
        const sortedItems = onMoveDown(highlight);
        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>(
                    (item) => ({
                        id: item.id,
                        fieldName: 'order',
                        newValue: item.newOrder,
                    }),
                ),
            );
        }
    };

    const handleSaveHighlights = async (purchasedPhotos?: CMSMedia[]) => {
        setIsSaving(true);

        try {
            if (!destination.id) {
                throw new Error('Destination ID is required');
            }

            const shutterstockSourceId = await getSourceId('shutterstock');

            // First, handle all media uploads (must be done sequentially)
            const highlightsWithMedia = await Promise.all(
                highlights.map(async (highlight) => {
                    let addedMedia = highlight.cms_media;

                    // If we have purchased photos, find the matching one for this highlight
                    if (
                        purchasedPhotos &&
                        highlight.cms_media?.source_id === shutterstockSourceId
                    ) {
                        const purchasedPhoto = purchasedPhotos.find(
                            (photo) =>
                                photo.external_id ===
                                highlight.cms_media?.external_id,
                        );

                        if (purchasedPhoto) {
                            addedMedia = purchasedPhoto;
                        }
                    }

                    // Add media if it doesn't have an ID (new media) or if it is a purchased photo
                    if (addedMedia && addedMedia.id === undefined) {
                        addedMedia = await addCMSMedia(
                            addedMedia,
                            HIGHLIGHT_COVER,
                        );
                    }

                    return {
                        ...highlight,
                        cms_media_id: addedMedia ? addedMedia.id : null,
                        cms_media: addedMedia,
                    };
                }),
            );

            // Now bulk save all highlights at once
            const savedContents = await saveBulkContent({
                destinationId: destination.id,
                contents: highlightsWithMedia.map((highlight) => {
                    const contentId =
                        highlight.id > 0 ? highlight.id : undefined;
                    return {
                        ...highlight,
                        id: contentId,
                        translations: highlight.translations,
                    };
                }),
            });

            // Filter to only get highlights (API might return other content types)
            const savedHighlights = savedContents.filter(
                (content) =>
                    content.content_type === DestinationContentTypes.HIGHLIGHT,
            );

            // Update local state with saved highlights
            setHighlights(savedHighlights);

            // Update the destination - remove old highlights and add saved ones
            const nonHighlightContents = (destination.contents ?? []).filter(
                (content) =>
                    content.content_type !== DestinationContentTypes.HIGHLIGHT,
            );

            updateDestination({
                ...destination,
                contents: [...nonHighlightContents, ...savedHighlights],
            });

            notification.success({
                message: 'Highlights saved successfully',
                key: 'highlights-saved',
            });

            // Update original values after successful save
            updateOriginalValues();
        } catch (error) {
            notification.error({
                message: 'Error saving highlights',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        setIsSubmitted(true);
        const shutterstockSourceId = await getSourceId('shutterstock');

        if (!validateFields(highlights)) {
            notification.error({
                message: 'Please fix the errors before saving',
            });
            return;
        }

        const imagesNeedingPurchase = highlights.flatMap((highlight) =>
            highlight.cms_media?.source_id === shutterstockSourceId &&
            !highlight.cms_media?.id
                ? [highlight.cms_media]
                : [],
        );

        if (imagesNeedingPurchase.length > 0) {
            setImagesToPurchase(imagesNeedingPurchase);
            setIsAddingStockPhotoWarningShown(true);
        } else {
            await handleSaveHighlights();
        }
    };

    const handleAddPurchasedPhotos = async (purchasedPhotos: CMSMedia[]) => {
        setIsAddingStockPhotoWarningShown(false);
        setImagesToPurchase([]);
        await handleSaveHighlights(purchasedPhotos);
    };

    if (!destination) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <SortableList
                title="Highlights"
                data={highlights}
                onAdd={handleAdd}
                onDelete={handleDelete}
                onMoveDown={handleMoveDown}
                onMoveUp={handleMoveUp}
                isLoading={isSaving || isDeleting}
            >
                {({ data, index }) =>
                    !data ? (
                        <SortableList.EmptyState
                            message="No highlights yet"
                            action={
                                <SortableList.AddButton
                                    onClick={handleAdd}
                                    disabled={isSaving}
                                >
                                    Add the first highlight
                                </SortableList.AddButton>
                            }
                        />
                    ) : (
                        <DestinationEditorHighlightItem
                            key={data.id}
                            index={index}
                            highlight={data}
                            onChangeField={handleUpdateField}
                            onChangeTranslations={handleUpdateTranslationField}
                            isSubmitted={isSubmitted}
                            errors={fieldErrors[data.id] ?? {}}
                        />
                    )
                }
            </SortableList>
            {isAddingStockPhotoWarningShown && imagesToPurchase.length > 0 ? (
                <PurchaseStockPhotoWarningModal
                    photosToPurchase={imagesToPurchase}
                    onAdd={handleAddPurchasedPhotos}
                    onCancel={() => {
                        setIsAddingStockPhotoWarningShown(false);
                        setImagesToPurchase([]);
                    }}
                    sizesToUpload={HIGHLIGHT_COVER}
                />
            ) : null}
            <DestinationEditorTabActions
                tabKey="highlights"
                saveLabel="Save Highlights"
                isSaving={isSaving}
                isSaveDisabled={isDeleting || !isDirty}
                onCancel={() => onClickDiscard(isDirty)}
                onSave={handleSave}
            />
        </>
    );
}

type DestinationEditorHighlightItemProps = {
    index: number;
    highlight: DestinationContentSchema;
    onChangeField: (change: UpdateDestinationContentFieldChange) => void;
    onChangeTranslations: (value: UpdateTranslationsValue) => void;
    isSubmitted: boolean;
    errors?: DestinationContentFieldErrors;
};

function DestinationEditorHighlightItem({
    index,
    highlight,
    onChangeField,
    onChangeTranslations,
    isSubmitted,
    errors = {},
}: DestinationEditorHighlightItemProps) {
    const { destination, location, locale } = useDestinationEditor();
    const {
        translatedContent: highlightValue,
        isTranslation,
        handleUpdateTranslationField,
    } = useDestinationContentTranslation({
        content: highlight,
        locale,
    });

    const handleChangeField = useCallback(
        <T extends keyof DestinationContentSchema>(
            fieldName: T,
            newValue: DestinationContentSchema[T],
        ) => {
            if (
                (fieldName === 'title' || fieldName === 'body') &&
                isTranslation
            ) {
                onChangeTranslations({
                    contentId: highlight.id,
                    translations: handleUpdateTranslationField({
                        [fieldName]: newValue,
                    }),
                });
            } else {
                onChangeField({
                    id: highlight.id,
                    fieldName: fieldName,
                    newValue: newValue,
                });
            }
        },
        [
            isTranslation,
            onChangeTranslations,
            highlight.id,
            handleUpdateTranslationField,
            onChangeField,
        ],
    );

    return (
        <div className={classNames(styles.item, 'js-highlight-item-editor')}>
            <Typography.Title level={4}>{`Item ${index + 1}`}</Typography.Title>
            <div className={styles.itemContent}>
                <div className={styles.mediaSelector}>
                    <MediaSelector
                        initialMediaSource="UNSPLASH"
                        location={location}
                        guideId={destination.guide_id ?? undefined}
                        onSelect={(media) => {
                            handleChangeField('cms_media', media);
                        }}
                        onDelete={() => handleChangeField('cms_media', null)}
                        media={highlight.cms_media ?? null}
                        ratio={HIGHLIGHT_COVER_RATIO}
                    />
                </div>
                <div className={styles.form}>
                    <FormField
                        label="Title"
                        labelWidth={100}
                        error={isSubmitted ? errors.title : undefined}
                        length={highlightValue.title?.length ?? 0}
                        maxLength={GUIDE_HIGHLIGHT_TITLE_MAX_LENGTH}
                    >
                        <Input
                            className="form-field__textarea"
                            value={highlightValue.title ?? ''}
                            onChange={(e) =>
                                handleChangeField('title', e.target.value)
                            }
                            status={isSubmitted && errors.title ? 'error' : ''}
                        />
                    </FormField>
                    <FormField
                        label="Description"
                        labelWidth={100}
                        length={highlightValue.body?.length ?? 0}
                        maxLength={GUIDE_HIGHLIGHT_DESCRIPTION_MAX_LENGTH}
                    >
                        <Input.TextArea
                            className="form-field__textarea"
                            value={highlightValue.body ?? ''}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            onChange={(e) =>
                                handleChangeField('body', e.target.value)
                            }
                        />
                    </FormField>
                </div>
            </div>
        </div>
    );
}

function getEmptyHighlight(
    destinationId: number,
    order: number,
): DestinationContentSchema {
    return {
        // Use negative id to ensure new highlights are added at the end, this is needed because the List component uses id as the key
        id: -order,
        destination_id: destinationId,
        content_type: DestinationContentTypes.HIGHLIGHT,
        order,
        body: '',
        source: DestinationContentSources.MANUAL,
        title: '',
        cms_media: null,
        translations: [],
    };
}
